import type { CardId, Suit } from './cards.js';
import { legalCards, role8Of, setScores } from './rules.js';
import {
  KITTY_SIZE,
  SEATS,
  targetOf,
  type DealResult,
  type ExchangeTransfer,
  type Match,
  type Phase,
  type Seat,
  type Target,
  type Trick,
} from './types.js';

/**
 * Bir oyuncunun görebileceği durum.
 *
 * Sunucudan istemciye SADECE bu gidiyor. Tam `Match` nesnesi asla ağa çıkmaz —
 * rakiplerin elleri, gömülen kartlar ve henüz açılmamış yer orada duruyor.
 * Tip ayrımı bilerek keskin: yanlışlıkla `Match` göndermek derleme hatası
 * versin.
 */
export interface PlayerView {
  seat: Seat;
  phase: Phase;
  hand: CardId[];
  /** Her koltuğun elindeki kart SAYISI — kartların kendisi değil. */
  handCounts: number[];
  trump: Suit | null;
  /** Bu elin 8'cisi; hedefler bundan türüyor. */
  role8: Seat;
  /** Koltuk başına hedef el sayısı (8 / 5 / 3). */
  targets: Target[];
  /** Koltuk başına şu ana kadar alınan el sayısı. */
  tricks: number[];
  /** Tamamlanan tur sayısı — kalan tur = 16 − bu. */
  trickCount: number;
  trick: Trick;
  turn: Seat;
  /** Bu elde oynanmış tüm kartlar — herkesin gördüğü bilgi. */
  played: CardId[];
  scores: number[];
  /** İçinde bulunulan setin puanları — alışveriş önceliğini bu belirliyor. */
  setScores: number[];
  dealIndex: number;
  set: number;
  /** Sıra sendeyse dolu; UI'daki kart vurgulaması bunun üzerinden çalışıyor. */
  legalCards: CardId[];
  /** Sen 8'ciysen ve gömme sırasındaysan kaç kart gömeceğin. Değilse 0. */
  buryCount: number;
  /** Gömdüğün kartlar — yalnızca 8'ci görür, diğerleri için boş. */
  buried: CardId[];
  exchange: ExchangeView | null;
  /**
   * Bu elde SENİN taraf olduğun alışverişler. Üçüncü oyuncu bunları görmüyor:
   * kaç kartın el değiştirdiği puanlardan zaten belli ama hangi kartlar
   * olduğu iki tarafın arasında kalıyor.
   */
  myExchange: ExchangeTransfer[];
  /** Tamamlanan her elin kaydı — puan çizelgesinin kaynağı. */
  dealResults: DealResult[];
}

export interface ExchangeView {
  /** Şu an seçim yapması beklenen oyuncu — herkes görüyor. */
  turn: Seat;
  /** Senden bekleniyorsa kime kaç kart vereceğin; beklenmiyorsa null. */
  mustGive: { to: Seat; count: number }[] | null;
  /**
   * Kimin kimden kaç kart alacağı — kartlar değil, yalnızca adetler.
   * Bu herkese açık: sapmalar zaten puan tablosunda görünüyor.
   */
  plan: { from: Seat; to: Seat; count: number }[];
}

const EMPTY_TRICK = (leader: Seat): Trick => ({ leader, plays: [] });

export function publicView(match: Match, seat: Seat): PlayerView {
  const deal = match.deal;
  const role8 = deal?.role8 ?? role8Of(match, match.dealIndex);
  const isRole8 = deal !== null && seat === deal.role8;

  return {
    seat,
    phase: match.phase,
    hand: deal ? [...deal.hands[seat]!] : [],
    handCounts: SEATS.map((s) => (deal ? deal.hands[s]!.length : 0)),
    trump: deal?.trump ?? null,
    role8,
    targets: SEATS.map((s) => targetOf(role8, s)),
    tricks: deal ? [...deal.tricks] : [0, 0, 0],
    trickCount: deal?.trickCount ?? 0,
    trick: deal ? deal.trick : EMPTY_TRICK(role8),
    turn: deal?.turn ?? seat,
    played: deal ? [...deal.played] : [],
    scores: [...match.scores],
    setScores: setScores(match),
    dealIndex: match.dealIndex,
    set: Math.floor(match.dealIndex / match.config.dealsPerSet) + 1,
    legalCards:
      deal && match.phase === 'play' && deal.turn === seat ? legalCards(deal, seat) : [],
    buryCount: match.phase === 'bury' && isRole8 ? KITTY_SIZE : 0,
    buried: isRole8 ? [...deal.buried] : [],
    exchange: exchangeView(match, seat),
    myExchange: (match.exchange?.done ?? [])
      .filter((t) => t.from === seat || t.to === seat)
      .map((t) => ({ ...t, given: [...t.given], received: [...t.received] })),
    dealResults: match.dealResults.map((r) => ({
      ...r,
      tricks: [...r.tricks],
      deltas: [...r.deltas],
      scores: [...r.scores],
    })),
  };
}

function exchangeView(match: Match, seat: Seat): ExchangeView | null {
  const ex = match.exchange;
  if (!ex || match.phase !== 'exchange') return null;

  const step = ex.queue[0];
  if (!step) return null;

  return {
    turn: step.from,
    mustGive: step.from === seat ? step.gives.map((g) => ({ ...g })) : null,
    // Bekleyen adımların TAMAMI görünüyor: ikinci sıradaki isteyen de neyin
    // geleceğini biliyor, sadece sırasını bekliyor.
    plan: ex.queue.flatMap((s) => s.gives.map((g) => ({ from: s.from, to: g.to, count: g.count }))),
  };
}
